<template>
  <div class="view-container">
    <div class="main-layout">
      <!-- 左侧导航 -->
      <div class="left-panel">
        <div class="panel-header"><h4>消息分类</h4></div>
        <div class="nav-list">
          <div :class="['nav-item', { active: category === 'all' }]" @click="switchCategory('all')">
            <i class="fas fa-inbox"></i><span>全部消息</span>
            <span class="count-badge">{{ totalCount }}</span>
          </div>
          <div :class="['nav-item', { active: category === 'unread' }]" @click="switchCategory('unread')">
            <i class="fas fa-envelope"></i><span>未读消息</span>
            <span v-if="unreadCount > 0" class="count-badge unread">{{ unreadCount }}</span>
          </div>
          <div :class="['nav-item', { active: category === 'private' }]" @click="switchCategory('private')">
            <i class="fas fa-user"></i><span>私信</span>
            <span class="count-badge">{{ privateCount }}</span>
          </div>
        </div>
      </div>

      <!-- 右侧消息列表 -->
      <div class="right-panel">
        <div class="panel-header">
          <div class="header-left">
            <h4>{{ categoryTitle }}</h4>
            <div class="search-box">
              <input v-model="searchKeyword" @keyup.enter="searchMessages" class="form-control form-control-sm" placeholder="搜索消息...">
              <button @click="searchMessages" class="btn-search"><i class="fas fa-search"></i></button>
            </div>
          </div>
          <div class="header-right">
            <button @click="markAllAsRead" class="btn btn-default btn-sm"><i class="fas fa-check-double"></i> 全部已读</button>
            <button @click="markSelectedAsRead" class="btn btn-default btn-sm"><i class="fas fa-check"></i> 标为已读</button>
            <button @click="deleteSelected" class="btn btn-danger btn-sm"><i class="fas fa-trash"></i> 删除</button>
          </div>
        </div>

        <div class="message-list">
          <table class="msg-table">
            <thead>
              <tr>
                <th width="40"><input type="checkbox" v-model="selectAll" @change="toggleSelectAll"></th>
                <th width="80">状态</th>
                <th>主题</th>
                <th width="180">时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="msg in messages" :key="msg.id" :class="{ unread: !msg.read }" @dblclick="openMessage(msg)">
                <td><input type="checkbox" v-model="msg.selected" @click.stop></td>
                <td>
                  <span :class="['status-badge', msg.read ? 'read' : 'unread']">
                    {{ msg.read ? '已读' : '未读' }}
                  </span>
                </td>
                <td class="subject-cell">{{ msg.title }}</td>
                <td class="time-cell">{{ msg.createTime }}</td>
              </tr>
              <tr v-if="messages.length === 0">
                <td colspan="4" class="empty-cell">暂无消息</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination">
          <button @click="prevPage" :disabled="pageIndex === 1" class="btn btn-default btn-sm">上一页</button>
          <span class="page-info">第 {{ pageIndex }} 页，共 {{ totalPages }} 页</span>
          <button @click="nextPage" :disabled="pageIndex >= totalPages" class="btn btn-default btn-sm">下一页</button>
        </div>
      </div>
    </div>

    <!-- 消息详情弹窗 -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="showDetailModal = false">
      <div class="modal-dialog">
        <div class="modal-header">
          <h4>{{ currentMessage?.title }}</h4>
          <button @click="showDetailModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="detail-meta">
            <div class="meta-row"><label>发送人：</label><span>{{ currentMessage?.senderName || currentMessage?.sendAccount }}</span></div>
            <div class="meta-row"><label>时间：</label><span>{{ currentMessage?.createTime }}</span></div>
          </div>
          <div class="detail-content" v-html="currentMessage?.content"></div>
        </div>
        <div class="modal-footer">
          <button @click="showDetailModal = false" class="btn btn-default">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getMessages, getUnreadMessages, getPrivateMessages, getMessageDetail, markMessagesAsRead, markAllAsRead as markAllAsReadAPI, deleteMessages } from '../api/message'

const category = ref('all')
const searchKeyword = ref('')
const pageIndex = ref(1)
const pageSize = ref(50)
const totalCount = ref(0)
const unreadCount = ref(0)
const privateCount = ref(0)
const messages = ref([])
const selectAll = ref(false)
const loading = ref(false)
const showDetailModal = ref(false)
const currentMessage = ref(null)

const categoryTitle = computed(() => {
  switch (category.value) {
    case 'all': return '全部消息'
    case 'unread': return '未读消息'
    case 'private': return '私信'
    default: return '消息中心'
  }
})

const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))

/**
 * 切换分类
 */
const switchCategory = (cat) => {
  category.value = cat
  pageIndex.value = 1
  searchKeyword.value = ''
  loadMessages()
}

/**
 * 加载消息统计
 */
const loadMessageStats = async () => {
  try {
    const statsResponse = await getMessages({ pageIndex: 1, pageSize: 1 })
    if (statsResponse.data) {
      unreadCount.value = statsResponse.data.unreadCount || 0
    }
    
    // 加载私信统计
    const privateResponse = await getPrivateMessages({ pageIndex: 1, pageSize: 1 })
    if (privateResponse.data) {
      privateCount.value = privateResponse.data.total || 0
    }
  } catch (error) {
    console.error('加载统计失败:', error)
  }
}

/**
 * 加载消息列表
 */
const loadMessages = async () => {
  try {
    loading.value = true
    let response
    
    const params = {
      pageIndex: pageIndex.value,
      pageSize: pageSize.value,
      keyword: searchKeyword.value || undefined
    }
    
    switch (category.value) {
      case 'unread':
        response = await getUnreadMessages(params)
        break
      case 'private':
        response = await getPrivateMessages(params)
        break
      default:
        response = await getMessages(params)
    }
    
    if (response.data) {
      totalCount.value = response.data.total || 0
      
      // 更新统计数据
      if (response.data.unreadCount !== undefined) {
        unreadCount.value = response.data.unreadCount
      }
      
      messages.value = (response.data.messages || []).map(msg => ({
        id: msg.Id,
        title: msg.Titles || '(无主题)',
        content: msg.Contents || '',
        sendAccount: msg.SendAccount,
        senderName: msg.SendAccount,
        read: msg.IsRead === 1,
        createTime: formatDateTime(msg.CreateTime),
        selected: false
      }))
    }
  } catch (error) {
    console.error('加载消息失败:', error)
    $message.error('加载消息失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 搜索消息
 */
const searchMessages = () => {
  pageIndex.value = 1
  loadMessages()
}

/**
 * 全选/取消全选
 */
const toggleSelectAll = () => {
  messages.value.forEach(msg => {
    msg.selected = selectAll.value
  })
}

/**
 * 标记所选为已读
 */
const markSelectedAsRead = async () => {
  const selected = messages.value.filter(m => m.selected && !m.read)
  if (selected.length === 0) {
    $message.warning('请至少选择一条未读消息')
    return
  }
  
  try {
    loading.value = true
    const ids = selected.map(m => m.id).join(',')
    await markMessagesAsRead(ids)
    
    selected.forEach(m => {
      m.read = true
      m.selected = false
    })
    
    unreadCount.value = Math.max(0, unreadCount.value - selected.length)
    selectAll.value = false
    $message.success('标记成功')
  } catch (error) {
    console.error('标记失败:', error)
    $message.error('标记失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 全部标为已读
 */
const markAllAsRead = async () => {
  if (unreadCount.value === 0) {
    $message.info('没有未读消息')
    return
  }
  
  if (!await $confirm(`确定将所有${unreadCount.value}条未读消息标记为已读吗？`)) return
  
  try {
    loading.value = true
    await markAllAsReadAPI()
    
    messages.value.forEach(m => {
      m.read = true
    })
    
    unreadCount.value = 0
    $message.success('全部标记成功')
  } catch (error) {
    console.error('标记失败:', error)
    $message.error('标记失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 删除所选消息
 */
const deleteSelected = async () => {
  const selected = messages.value.filter(m => m.selected)
  if (selected.length === 0) {
    $message.warning('请至少选择一条消息')
    return
  }
  
  if (!await $confirm(`确定删除所选的${selected.length}条消息吗？`)) return
  
  try {
    loading.value = true
    const ids = selected.map(m => m.id).join(',')
    await deleteMessages(ids)
    
    messages.value = messages.value.filter(m => !m.selected)
    totalCount.value = Math.max(0, totalCount.value - selected.length)
    selectAll.value = false
    $message.success('删除成功')
  } catch (error) {
    console.error('删除失败:', error)
    $message.error('删除失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 打开消息详情
 */
const openMessage = async (msg) => {
  try {
    loading.value = true
    const response = await getMessageDetail(msg.id)
    
    if (response.data) {
      currentMessage.value = {
        title: response.data.Titles,
        content: response.data.Contents,
        sendAccount: response.data.SendAccount,
        senderName: response.data.SendAccount,
        createTime: formatDateTime(response.data.CreateTime)
      }
      
      showDetailModal.value = true
      
      // 更新为已读状态
      if (!msg.read) {
        msg.read = true
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    }
  } catch (error) {
    console.error('加载消息详情失败:', error)
    $message.error('加载消息详情失败：' + (error.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

/**
 * 上一页
 */
const prevPage = () => {
  if (pageIndex.value > 1) {
    pageIndex.value--
    loadMessages()
  }
}

/**
 * 下一页
 */
const nextPage = () => {
  if (pageIndex.value < totalPages.value) {
    pageIndex.value++
    loadMessages()
  }
}

/**
 * 格式化日期时间
 */
const formatDateTime = (datetime) => {
  if (!datetime) return ''
  const d = new Date(datetime)
  return d.toLocaleString('zh-CN')
}

onMounted(() => {
  loadMessageStats()
  loadMessages()
})
</script>

<style scoped>
.view-container { padding: 16px; background: #f5f7fa; }
.main-layout { display: flex; gap: 16px; height: calc(100vh - 140px); }

/* 左侧导航 */
.left-panel { flex: 0 0 220px; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; overflow: hidden; }
.panel-header { padding: 12px 16px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; }
.panel-header h4 { margin: 0; font-size: 14px; font-weight: 600; color: #374151; }
.nav-list { padding: 8px 0; }
.nav-item { display: flex; align-items: center; gap: 10px; padding: 10px 16px; cursor: pointer; font-size: 13px; color: #374151; transition: all 0.15s; }
.nav-item:hover { background: #f3f4f6; }
.nav-item.active { background: #eff6ff; color: #2563eb; font-weight: 500; }
.nav-item i { width: 18px; text-align: center; }
.count-badge { margin-left: auto; background: #e5e7eb; color: #6b7280; padding: 2px 8px; border-radius: 10px; font-size: 11px; }
.count-badge.unread { background: #ef4444; color: #fff; }

/* 右侧面板 */
.right-panel { flex: 1; background: #fff; border-radius: 6px; border: 1px solid #e8ecf1; display: flex; flex-direction: column; }
.panel-header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid #e8ecf1; }
.header-left { display: flex; align-items: center; gap: 16px; }
.header-right { display: flex; gap: 8px; }
.search-box { display: flex; gap: 4px; }
.form-control-sm { padding: 5px 10px; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 12px; width: 200px; }
.btn-search { padding: 5px 12px; background: #3b82f6; color: #fff; border: none; border-radius: 4px; cursor: pointer; }

/* 消息列表 */
.message-list { flex: 1; overflow-y: auto; }
.msg-table { width: 100%; border-collapse: collapse; }
.msg-table thead th { padding: 10px 12px; background: #f9fafb; border-bottom: 1px solid #e8ecf1; font-size: 12px; font-weight: 600; color: #6b7280; text-align: left; position: sticky; top: 0; z-index: 1; }
.msg-table tbody td { padding: 12px; border-bottom: 1px solid #f3f4f6; font-size: 13px; color: #374151; }
.msg-table tbody tr { cursor: pointer; transition: background 0.15s; }
.msg-table tbody tr:hover { background: #f9fafb; }
.msg-table tbody tr.unread { background: #fffdf5; }
.msg-table tbody tr.unread:hover { background: #fef3c7; }
.status-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: 500; }
.status-badge.read { background: #e5e7eb; color: #6b7280; }
.status-badge.unread { background: #fef3c7; color: #d97706; }
.subject-cell { font-weight: 500; }
.time-cell { color: #9ca3af; font-size: 12px; }
.empty-cell { text-align: center; padding: 40px; color: #9ca3af; }

/* 分页 */
.pagination { padding: 12px 16px; border-top: 1px solid #e8ecf1; display: flex; justify-content: center; align-items: center; gap: 16px; }
.page-info { font-size: 13px; color: #6b7280; }

/* 按钮 */
.btn { padding: 6px 12px; border: 1px solid transparent; border-radius: 4px; font-size: 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.2s; }
.btn-default { background: #fff; color: #374151; border-color: #d1d5db; }
.btn-default:hover { background: #f9fafb; }
.btn-default:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-danger { background: #ef4444; color: #fff; border-color: #ef4444; }
.btn-danger:hover { background: #dc2626; }
.btn-sm { padding: 5px 10px; font-size: 12px; }

/* 弹窗 */
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-dialog { background: #fff; border-radius: 8px; width: 700px; max-height: 85vh; overflow-y: auto; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e7eaec; }
.modal-header h4 { margin: 0; font-size: 16px; }
.modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #999; }
.modal-close:hover { color: #333; }
.modal-body { padding: 20px; }
.detail-meta { margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #e8ecf1; }
.meta-row { display: flex; gap: 8px; padding: 4px 0; font-size: 13px; }
.meta-row label { font-weight: 600; color: #374151; min-width: 60px; }
.meta-row span { color: #6b7280; }
.detail-content { font-size: 14px; line-height: 1.8; color: #374151; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; }
</style>
